/**
 * ActionCard — ONE action of the reshaped mortgagee launch contract.
 *
 * The card does NOT implement a field renderer. It delegates every widget to the
 * shared `JsonSchemaForm` in controlled mode (PAD-1), which already owns the
 * confidence chips, the per-field override / hide / disable / error hooks and the
 * split-address item overrides today's flat mortgagee form depends on. Writing a
 * second renderer would fork the widget set across two hosts, which
 * `ui/CLAUDE.md`'s parity rule and design Constraint 20 forbid.
 *
 * Two structural rules:
 *
 *  - **No footer.** `onSubmit` AND `onCancel` are omitted, so the renderer's
 *    `showFooter = !readOnly && ((onSubmit && !hasPillField) || onCancel)` is
 *    false. Passing `onCancel` alone would resurrect the footer and give the AM a
 *    second way to commit from inside an action. Exactly one submit control
 *    exists on the whole form, and it belongs to `LaunchForm`.
 *  - **Every key is DECLARED, never spelled.** The records list, the change type,
 *    the interest type, the target interest and the term identity are addressed
 *    through `binding.roles.*`. A copied `mortgagees` / `mortgagees.mailing_state`
 *    literal attaches to nothing when the contract declares `interests`, and the
 *    split State+ZIP line plus the hide-on-add/delete rules silently no-op.
 *
 * Every prop of today's invocation (`App.tsx`'s mortgagee `SchemaForm`) is
 * carried over, scoped to the action slice. A prop present there and absent here
 * is a regression, not a simplification — `fieldDisabled` is AC-8's locked
 * derived fields, and the `splitAddressSchema` wrapper is what gives the split
 * State+ZIP overrides fields to attach to. Policy Number and Effective Date
 * render on the policy group, not here.
 */

import { useMemo } from "react";
import type { ReactNode } from "react";
import JsonSchemaForm, {
  type ConfidenceLevel,
} from "~/common/workflows/components/JsonSchemaForm";
import type { FieldSchema } from "~/common/workflows/schemaHelpers";
import type { PolicyInForce } from "~/common/workflows/types/client";
import { splitAddressSchema } from "~/common/workflows/launch/addressParts";
import {
  readInterestOptionsForSelection,
  type InterestOption,
} from "~/common/workflows/launch/interestTargetOptions";
import type { LaunchBinding } from "~/common/workflows/launch/launchSchemaBinding";
import OldMortgageeSelect from "~/common/workflows/launch/OldMortgageeSelect";
import StateSelect from "~/common/workflows/launch/StateSelect";
import InterestTypeField from "~/common/workflows/launch/InterestTypeField";
import { policyTermFieldOverride } from "~/common/workflows/launch/PolicyTermField";
import {
  COMPACT_FIELD_LABEL,
  COMPACT_SINGLE_LINE,
} from "~/common/workflows/launch/launchDensity";

/**
 * The run-level key the "which existing interest is this about" control writes
 * alongside `target_interest`.
 *
 * It is deliberately NOT a `LAUNCH_ROLE_CANDIDATES` role: it is not a value the
 * form must have a declared home for (the payload is complete without it), and
 * adding it as a role would put it in `missingRoles` — a loud banner — for every
 * contract that omits it. `launchSchemaBinding.CARD_RENDERED_RUN_LEVEL_KEYS`
 * carries the same literal, and that is the ONE place the pairing is documented:
 * this control is why the key is not reported as unbound.
 */
export const OLD_INTEREST_NAME_KEY = "old_mortgagee_name";

/**
 * Presentation-only un-hide of ONE declared property.
 *
 * `interest_type` is published with `x-hidden: true` — both in the hand-authored
 * fixture and in the schema `action-list-contract` actually dumps — so
 * `parseObjectSchema` drops it and a `fieldOverride` keyed on it would attach to
 * nothing. The amended AC-4 (operator, 2026-08-26) requires that field to be a
 * VISIBLE declared choice, so the card reveals it the same way
 * `splitAddressSchema` rewrites the address row: on a CLONE, for presentation
 * only. The wire schema, `declaredKeys` and the payload are untouched — an
 * `x-hidden` property was always in `declaredKeys.action`, so the value travelled
 * before this and travels after it.
 *
 * Recorded as a cross-story seam: `action-list-contract` owns whether
 * `interest_type` keeps its `x-hidden` annotation.
 */
function revealDeclaredField(
  schema: Record<string, unknown> | null,
  key: string | null,
): Record<string, unknown> | null {
  if (!schema || !key) return schema;
  const props = schema.properties as Record<string, unknown> | undefined;
  const def = props?.[key] as Record<string, unknown> | undefined;
  if (!def || def["x-hidden"] !== true) return schema;
  const revealed = { ...def };
  delete revealed["x-hidden"];
  return { ...schema, properties: { ...props, [key]: revealed } };
}

/**
 * Display-only retitle of declared properties, on a CLONE (never the wire
 * schema). A key is a top-level action property (`target_interest`) or a record
 * sub-field addressed `<recordsKey>.<prop>` (`mortgagees.name`,
 * `mortgagees.mailing_street`); the sub-field path follows the array's `items`,
 * through a `$ref` into the clone's own `$defs`.
 */
function relabelSchema(
  schema: Record<string, unknown> | null | undefined,
  overrides: Record<string, string> | undefined,
  recordsKey: string | null,
): Record<string, unknown> | null | undefined {
  if (!schema || !overrides || Object.keys(overrides).length === 0) {
    return schema;
  }
  const clone = JSON.parse(JSON.stringify(schema)) as Record<string, unknown>;
  const deref = (node: unknown): Record<string, unknown> | undefined => {
    if (!node || typeof node !== "object") return undefined;
    const n = node as Record<string, unknown>;
    if (typeof n.$ref !== "string") return n;
    let cur: unknown = clone;
    for (const seg of n.$ref.replace(/^#\//, "").split("/")) {
      cur = (cur as Record<string, unknown> | undefined)?.[seg];
    }
    return cur as Record<string, unknown> | undefined;
  };
  const props = clone.properties as Record<string, unknown> | undefined;
  const recordProps = recordsKey
    ? (deref(
        deref((props?.[recordsKey] as Record<string, unknown>)?.items),
      )?.properties as Record<string, unknown> | undefined)
    : undefined;
  for (const [path, title] of Object.entries(overrides)) {
    const dot = recordsKey ? path.indexOf(`${recordsKey}.`) : -1;
    const def =
      dot === 0 && recordsKey
        ? recordProps?.[path.slice(recordsKey.length + 1)]
        : props?.[path];
    // A top-level node may itself be a `$ref` wrapper; the title lives on the
    // property definition the form reads.
    if (def && typeof def === "object") {
      (def as Record<string, unknown>).title = title;
    }
  }
  return clone;
}

interface ItemOverrideArgs {
  field: FieldSchema;
  value: unknown;
  setValue: (key: string, next: unknown) => void;
  readOnly: boolean;
  index: number;
  item: Record<string, unknown>;
}

/**
 * State + ZIP on ONE line (operator request 2026-08-03).
 *
 * A RENDER CALLBACK, not a component — `JsonSchemaForm` calls it to build a
 * sub-field row and never mounts it — hence the plain function and the explicit
 * `actionIndex` / `submitting` arguments rather than a props object.
 *
 * It writes BOTH keys through the row's setter; the paired `mailing_zip`
 * override suppresses the duplicate default ZIP row. ⚠️ ONE write per event
 * tick: the setter maps over the render's item snapshot, so each key is driven
 * from its own user event.
 */
function renderStateZipRow(
  { field, value, setValue, readOnly, index: rowIndex, item }: ItemOverrideArgs,
  actionIndex: number,
  submitting: boolean,
  zipLabel = "ZIP",
): ReactNode {
  const zipId = `mortgagee-zip-${actionIndex}-${rowIndex}`;
  // Two columns ALWAYS (story 28633): `sm:` is a viewport breakpoint and the
  // Outlook pane is always narrower than it, so State and ZIP used to stack.
  return (
    <div className="grid grid-cols-2 gap-2">
      {/* Filterable "Michigan | MI" combobox — commits only valid USPS codes,
          so the composed address always parses. */}
      <StateSelect
        label={field.label}
        value={typeof value === "string" ? value : ""}
        disabled={readOnly || submitting}
        onCommit={(code) => setValue(field.key, code)}
        idSuffix={`${actionIndex}-${rowIndex}`}
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <label htmlFor={zipId} className={COMPACT_FIELD_LABEL}>
          {zipLabel}
        </label>
        <input
          id={zipId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={typeof item.mailing_zip === "string" ? item.mailing_zip : ""}
          disabled={readOnly || submitting}
          onChange={(e) => setValue("mailing_zip", e.target.value)}
          className={`w-full touch-target ${COMPACT_SINGLE_LINE}`}
          data-testid={zipId}
        />
      </div>
    </div>
  );
}

export interface ActionCardProps {
  binding: LaunchBinding;
  /** Position in the action list — DOM ids and testids are per-row. */
  index: number;
  /** The action slice itself. `JsonSchemaForm` renders THIS, not the merge. */
  action: Record<string, unknown>;
  /**
   * `mergeLaunchSlice(binding, runLevelValues, action)` — the READ view every
   * lifted helper needs, because the term identity lives on the action while
   * `policy_options` / `interest_options_by_line` stay at run level. A card that
   * called `readInterestOptionsForSelection(action)` would render an empty
   * target-interest select with no error anywhere. NEVER spread back into the
   * action.
   */
  helperValues: Record<string, unknown>;
  /** Replace the action slice (the renderer's `onChange`). */
  onChangeAction: (next: Record<string, unknown>) => void;
  /**
   * Write a MIXED patch. `LaunchForm` routes each key to the slice that declares
   * it — `target_interest` onto the action, `old_mortgagee_name` /
   * `policy_type` / `policy_term_key` to run level — so no control here has to
   * know where a key lives.
   */
  writePatch: (patch: Record<string, unknown>) => void;
  /** In-force option rows, already resolved by the host. */
  policyOptions: readonly PolicyInForce[];
  submitting?: boolean;
  /** Already PROJECTED onto this slice by `LaunchForm`. */
  fieldConfidence?: Record<string, ConfidenceLevel>;
  /** Already PROJECTED onto this slice by `LaunchForm`. */
  fieldLoading?: Record<string, boolean>;
  /** This action's entry from `validateLaunch().actionErrors`. */
  fieldErrors?: Record<string, string | null | undefined>;
  /**
   * True when the declared term identity lives on the ACTION, so the term
   * control renders inside this card. Exactly one term control renders per
   * launch (`LaunchForm` owns that decision).
   */
  renderTermField?: boolean;
  /** Label for the old-interest control, from the declared run-level property. */
  oldInterestLabel?: string;
  /**
   * Declared keys to leave out of the form (the v4 block owns the change-type
   * control and More options owns the interest type). Presentation only: the
   * value still lives on the slice and still submits. Absent = today's card.
   */
  hiddenKeys?: readonly string[];
  /**
   * Display-only retitles, never the wire schema: a top-level key, or a record
   * sub-field as `<recordsKey>.<prop>` (address parts included).
   */
  labelOverrides?: Record<string, string>;
  /** Open the existing-mortgagee listbox on mount. */
  defaultOpen?: boolean;
}

export default function ActionCard({
  binding,
  index,
  action,
  helperValues,
  onChangeAction,
  writePatch,
  policyOptions,
  submitting = false,
  fieldConfidence,
  fieldLoading,
  fieldErrors,
  renderTermField = false,
  oldInterestLabel,
  hiddenKeys,
  labelOverrides,
  defaultOpen,
}: ActionCardProps) {
  const { roles } = binding;
  const recordsKey = roles.interestRecords;
  const changeTypeKey = roles.changeType;
  const interestTypeKey = roles.interestType;
  const targetKey = roles.targetInterest;
  const effectiveDateKey = roles.effectiveDate;
  // Candidate order puts `policy_number` first, so `termIdentity[0]` is the
  // declared policy-number spelling whenever the contract declares one.
  const policyNumberKey = roles.termIdentity[0] ?? null;

  // Presentation-only schema: reveal the declared interest type, then rewrite
  // the record item's single `mailing_address` into the four part inputs. Both
  // rewrites are memoised — `splitAddressSchema` caches per (schema object,
  // records key), and a fresh clone every keystroke would defeat
  // `JsonSchemaForm`'s `useMemo(parseObjectSchema, [schema])`.
  const cardSchema = useMemo(() => {
    const revealed = revealDeclaredField(
      binding.actionItemSchema,
      interestTypeKey,
    );
    const split = recordsKey
      ? splitAddressSchema(revealed, recordsKey)
      : splitAddressSchema(revealed);
    // Display-only retitle (v4 block), on a clone: the wire schema is untouched.
    return relabelSchema(split, labelOverrides, recordsKey);
  }, [binding.actionItemSchema, interestTypeKey, recordsKey, labelOverrides]);

  const changeType =
    changeTypeKey && typeof action[changeTypeKey] === "string"
      ? (action[changeTypeKey] as string)
      : "";
  const policyNumber =
    policyNumberKey && typeof action[policyNumberKey] === "string"
      ? (action[policyNumberKey] as string)
      : "";
  const hasPolicyOptions = policyOptions.length >= 1;

  // Fed the MERGED record: the prefill echoes it reads (`policy_options`,
  // `interest_options_by_line`) stay at run level while the term keys moved onto
  // the action.
  const interestOptions = readInterestOptionsForSelection(helperValues);
  // >= 1, not >= 2 (operator correction 2026-08-03): the popout is the ONLY
  // place the interest's grid details surface and the name must match EPIC
  // exactly, so a single-option policy still needs the derived control.
  const showInterestPicker = !!policyNumber && interestOptions.length >= 1;
  const currentTarget = targetKey
    ? ((action[targetKey] as InterestOption | null | undefined) ?? null)
    : null;
  const oldInterestName =
    typeof helperValues[OLD_INTEREST_NAME_KEY] === "string"
      ? (helperValues[OLD_INTEREST_NAME_KEY] as string)
      : "";

  const fieldOverrides: Record<
    string,
    (args: {
      field: FieldSchema;
      value: unknown;
      setValue: (key: string, next: unknown) => void;
      readOnly: boolean;
    }) => ReactNode
  > = {};

  // The target-interest control. Today it is mounted on `old_mortgagee_name`
  // because `target_interest` renders hidden; under the reshaped contract
  // `old_mortgagee_name` is RUN level and only `target_interest` is in this
  // slice, so the control moves onto the key it actually drives and keeps
  // writing both (Phase 2's recorded gap 2, decided here).
  if (targetKey && showInterestPicker) {
    fieldOverrides[targetKey] = ({ field, readOnly }) => (
      <OldMortgageeSelect
        field={
          oldInterestLabel ? { ...field, label: oldInterestLabel } : field
        }
        options={interestOptions}
        target={currentTarget}
        name={oldInterestName}
        disabled={readOnly || submitting}
        defaultOpen={defaultOpen}
        onSelect={(opt) =>
          // The whole row, already snake_case — see `normalizeInterestOption`.
          // Writing the raw camelCase row blanks four of the five fields the
          // EPIC match compares on, with no error anywhere.
          writePatch({
            [targetKey]: opt,
            [OLD_INTEREST_NAME_KEY]: opt.name,
          })
        }
        onNotListed={() =>
          writePatch({ [targetKey]: null, [OLD_INTEREST_NAME_KEY]: "" })
        }
        onManualChange={(name) =>
          writePatch({ [targetKey]: null, [OLD_INTEREST_NAME_KEY]: name })
        }
      />
    );
  }

  // Interest type — the amended AC-4 control (task 4).
  if (interestTypeKey && binding.interestTypeValues.length > 0) {
    fieldOverrides[interestTypeKey] = ({ field, value, setValue, readOnly }) => (
      <InterestTypeField
        field={field}
        values={binding.interestTypeValues}
        value={value}
        changeType={changeType}
        target={currentTarget}
        disabled={readOnly || submitting}
        onChange={(next) => setValue(field.key, next)}
      />
    );
  }

  // The term control, when the declared term identity lives on the ACTION
  // (task 7). `LaunchForm` guarantees exactly one renders per launch.
  if (renderTermField && policyNumberKey && hasPolicyOptions) {
    fieldOverrides[policyNumberKey] = policyTermFieldOverride({
      binding,
      helperValues,
      policyOptions,
      writePatch,
      submitting,
    });
  }

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`launch-action-card-${index}`}
    >
      <JsonSchemaForm
        // Presentation-only rewrite: the record item's single `mailing_address`
        // renders as Street / City / State / ZIP. The wire schema is untouched —
        // `buildLaunchPayload` composes the canonical string back.
        schema={cardSchema}
        value={action}
        onChange={onChangeAction}
        // NO onSubmit and NO onCancel — see the footer rule at the top.
        submitting={submitting}
        // The pane's tight layout (story 28633) — opt-in, so ora's other
        // JsonSchemaForm pages keep theirs.
        density="compact"
        fieldConfidence={fieldConfidence}
        fieldLoading={fieldLoading}
        fieldOverrides={
          Object.keys(fieldOverrides).length > 0 ? fieldOverrides : undefined
        }
        fieldDisabled={
          // AC-8: term-DERIVED values are visible but not desyncable. Declared
          // per action only when the contract puts them there; the run-level
          // spellings are `TERM_DERIVED_KEYS` and are locked by the same rule
          // wherever they render.
          hasPolicyOptions
            ? {
                policy_type: true,
                carrier_name: true,
                carrier_id: true,
              }
            : undefined
        }
        fieldHidden={{
          // The v4 block hides the keys it owns elsewhere (see `hiddenKeys`).
          ...Object.fromEntries((hiddenKeys ?? []).map((k) => [k, true])),
          // Policy Number and Effective Date render at the policy-group
          // level, outside this action. Hiding is presentation — the values
          // still live on the action slice and still submit.
          ...(policyNumberKey ? { [policyNumberKey]: true } : {}),
          ...(effectiveDateKey ? { [effectiveDateKey]: true } : {}),
          // Progressive disclosure, keyed on the DECLARED change-type role.
          // A hidden field still validates and still submits — hiding is
          // presentation, never a value deletion.
          ...(targetKey && !showInterestPicker ? { [targetKey]: true } : {}),
          ...(changeType === "" && targetKey ? { [targetKey]: true } : {}),
          ...(changeType === "" && recordsKey ? { [recordsKey]: true } : {}),
          // Add: there is no existing interest to pick.
          ...(changeType === "add" && targetKey ? { [targetKey]: true } : {}),
          // Delete asks for no new record. The control hides; the VALUE must
          // still be sent — an omitted key is rejected server-side whereas []
          // validates.
          ...(changeType === "delete" && recordsKey
            ? { [recordsKey]: true }
            : {}),
          // Change/delete never touch interest type in EPIC, and the value
          // is not a trusted sourced field — omit the control entirely.
          ...((changeType === "update" || changeType === "delete") &&
          interestTypeKey
            ? { [interestTypeKey]: true }
            : {}),
        }}
        // Editable-first, read-only-last (operator redesign 2026-08-03). Every
        // entry is a key that may or may not exist — an unlisted or absent key
        // keeps schema order. Policy number and effective date are policy-level.
        fieldOrder={[
          ...(changeTypeKey ? [changeTypeKey] : []),
          ...(interestTypeKey ? [interestTypeKey] : []),
          ...(targetKey ? [targetKey] : []),
          ...(recordsKey ? [recordsKey] : []),
          "policy_type",
          "carrier_name",
        ]}
        // Schema descriptions are LLM-facing Pydantic field docs and read as
        // wall-of-text in a narrow task pane (operator request 2026-07-28).
        hideFieldDescriptions
        itemFieldOverrides={
          recordsKey
            ? {
                // State + ZIP share one line (operator request 2026-08-03):
                // this override renders BOTH controls and writes both keys
                // through the row's setter; the ZIP override below suppresses
                // the duplicate default row.
                //
                [`${recordsKey}.mailing_state`]: (args) =>
                  renderStateZipRow(
                    args,
                    index,
                    submitting,
                    labelOverrides?.[`${recordsKey}.mailing_zip`],
                  ),
                [`${recordsKey}.mailing_zip`]: () => null,
              }
            : undefined
        }
        fieldErrors={fieldErrors}
      />
    </div>
  );
}
